import { useMutation, useQuery } from '@apollo/client';
import { Button, SettingsGroup, SettingsItemTextField } from '@elipssolution/harfang';
import { styled } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import SettingsDialogPage from '../../../../../../components/SettingsDialogPage';
import { useSettingsDialog } from '../../../../../../hooks/useSettingsDialog';
import { DomainType } from '../../../../../../types/domain';
import { SUCCESS_ANIMATION_DELAY } from '../../../../../../utils/statusAnimationDelay';
import {
	CreateDomainBillExchangeSettingCounterpartAccountType,
	CREATE_DOMAIN_BILL_EXCHANGE_SETTING_COUNTERPART_ACCOUNT,
	FetchDomainBillExchangeSettingCounterpartAccountType,
	FETCH_DOMAIN_BILL_EXCHANGE_SETTING_COUNTERPART_ACCOUNT,
} from '../../../../api/settingBillExchangeCounterpartAccount';
import { AccountType } from '../../../../types/account';

const ActionWrapper = styled('div')({
	display: 'flex',
	flexDirection: 'row-reverse',
	justifyContent: 'space-between',
});

const ErrorWrapper = styled('div')(({ theme: { palette, shape } }) => ({
	height: 36,
	width: '50%',

	display: 'grid',
	placeItems: 'center',

	color: palette.error.main,
	backgroundColor: `${palette.error.main}1A`,
	borderRadius: shape.borderRadius,
}));

type FormFieldType = {
	accountCode: AccountType['code'];
};

type BillExchangeCounterpartProps = {
	domainId?: DomainType['id'];
};

const BillExchangeCounterpartAccount = ({ domainId }: BillExchangeCounterpartProps) => {
	const { onHasDirtyFormChange } = useSettingsDialog();

	const {
		control,
		formState: { isDirty, isValid },
		handleSubmit,
		setValue,
		reset,
	} = useForm<FormFieldType>();

	const isFormValid = useMemo(() => isDirty && isValid, [isDirty, isValid]);

	const [error, setError] = useState<string>();
	const [isCreateBillExchangeCounterpartAccountSucceeded, setIsCreateBillExchangeCounterpartAccountSucceeded] =
		useState(false);

	useQuery<FetchDomainBillExchangeSettingCounterpartAccountType>(
		FETCH_DOMAIN_BILL_EXCHANGE_SETTING_COUNTERPART_ACCOUNT,
		{
			variables: {
				id: domainId,
			},
			onCompleted: ({ quickentry_settingDomainBillExchangeCounterpartAccount: { accountCode } }) => {
				if (accountCode) {
					setValue('accountCode', accountCode, { shouldDirty: true });
				}
			},
		},
	);

	const [createBillExchangeCounterpartAccount] = useMutation<CreateDomainBillExchangeSettingCounterpartAccountType>(
		CREATE_DOMAIN_BILL_EXCHANGE_SETTING_COUNTERPART_ACCOUNT,
		{
			onCompleted: ({ quickentry_createSettingDomainBillExchangeCounterpartAccount: { accountCode } }) => {
				reset({ accountCode });
				setIsCreateBillExchangeCounterpartAccountSucceeded(true);
				setTimeout(() => setIsCreateBillExchangeCounterpartAccountSucceeded(false), SUCCESS_ANIMATION_DELAY);
			},
			onError: (mutationError) => {
				setError(mutationError.message);
			},
		},
	);

	const onSubmit = ({ accountCode }: FormFieldType) =>
		createBillExchangeCounterpartAccount({
			variables: {
				createSettingDomainBillExchangeCounterpartAccountInput: {
					accountCode,
					domainId,
				},
			},
		});

	useEffect(() => {
		onHasDirtyFormChange(isDirty);
	}, [isDirty, onHasDirtyFormChange]);

	return (
		<SettingsDialogPage title="Contrepartie LCR émises">
			<SettingsGroup>
				<Controller
					name="accountCode"
					control={control}
					rules={{
						pattern: {
							value: /^5/,
							message: 'Le code du compte est incorrect',
						},
						required: true,
					}}
					render={({ field }) => (
						<SettingsItemTextField
							{...field}
							label="Compte de contrepartie"
							description="Le code doit correspondre à un compte de classe 5."
							required
						/>
					)}
				/>
			</SettingsGroup>

			<ActionWrapper>
				<Button disabled={!isFormValid} onClick={handleSubmit(onSubmit)} variant="contained">
					{isCreateBillExchangeCounterpartAccountSucceeded ? 'Contrepartie validée' : 'Valider'}
				</Button>

				{error && <ErrorWrapper>{error}</ErrorWrapper>}
			</ActionWrapper>
		</SettingsDialogPage>
	);
};

export default BillExchangeCounterpartAccount;
